import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { store } from '../../store';
import { router } from '../../router';
import { t, setLocale } from '../../i18n';
import { showToast } from '../../components/ui/toast';
import '../../components/shared/auth-shell';
import type { SessionUser } from '../../store';

@customElement('cadence-login-page')
export class CadenceLoginPage extends LitElement {
  @state() private email = '';
  @state() private password = '';
  @state() private remember = false;
  @state() private loading = false;
  @state() private error = '';

  static styles = css`
    :host { display: block; }
    form { display: flex; flex-direction: column; gap: var(--space-4); }
    .row { display: flex; align-items: center; justify-content: space-between; font-size: 0.88rem; }
    .remember { display: flex; align-items: center; gap: 8px; color: var(--color-text-secondary); }
    .remember input { accent-color: var(--color-primary); }
    a { color: var(--color-primary); text-decoration: none; }
    a:hover { text-decoration: underline; }
    .form-error { color: var(--color-danger); font-size: 0.85rem; margin: 0; }
  `;

  private async submit() {
    if (!this.email || !this.password) {
      this.error = t('auth.login.required');
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      const { user, features, breakPeriod, school } = await api.post<{ user: SessionUser; features?: Record<string, boolean>; breakPeriod?: boolean; school?: { name: string; logo_url: string | null; default_language?: string } }>('/auth/login', {
        email: this.email,
        password: this.password,
        remember: this.remember,
      });
      store.set({ user, features: features ?? {}, breakPeriod: breakPeriod === true, school: school ?? null });
      setLocale(user.language_preference || school?.default_language || 'en');
      store.applyTheme(user.dark_mode);
      router.navigate('/admin/dashboard');
    } catch (err) {
      const message = err instanceof ApiError && err.status === 401 ? t('auth.login.invalid') : (err as Error).message;
      this.error = message;
      showToast(message, 'error');
    } finally {
      this.loading = false;
    }
  }

  render() {
    return html`
      <cadence-auth-shell heading=${t('auth.login.title')} sub=${t('auth.login.subtitle')}>
        <form @submit=${(e: Event) => { e.preventDefault(); this.submit(); }}>
          ${this.error ? html`<p class="form-error" role="alert">${this.error}</p>` : ''}
          <cadence-input
            label=${t('auth.login.email')}
            type="email"
            name="email"
            autocomplete="username"
            .value=${this.email}
            @input=${(e: CustomEvent) => (this.email = e.detail)}
            @enter=${() => this.submit()}
          ></cadence-input>
          <cadence-input
            label=${t('auth.login.password')}
            type="password"
            name="password"
            autocomplete="current-password"
            .value=${this.password}
            @input=${(e: CustomEvent) => (this.password = e.detail)}
            @enter=${() => this.submit()}
          ></cadence-input>
          <div class="row">
            <label class="remember">
              <input
                type="checkbox"
                .checked=${this.remember}
                @change=${(e: Event) => (this.remember = (e.target as HTMLInputElement).checked)}
              />
              ${t('auth.login.remember')}
            </label>
            <a href="/forgot-password" data-link>${t('auth.login.forgot')}</a>
          </div>
          <cadence-button type="submit" full-width ?loading=${this.loading} @click=${() => this.submit()}>
            ${t('auth.login.submit')}
          </cadence-button>
        </form>
      </cadence-auth-shell>
    `;
  }
}
